// askSmart-ui/src/pages/MapleCoin/operations/ExternalBankingTab.tsx
//
// External banking (Stage 10): what MapleCoin knows about its payment
// provider connection — configured or not, connected or not, which
// environment — and everything the provider has told us, with how each event
// was handled. Shows only real integration state: no provider balances (none
// are fetched yet), no placeholder figures, and Sandbox Funding is never
// presented as provider funds.
//
// Nothing here moves money. Closing an event records a reviewed decision to
// take no action.
import { useCallback, useEffect, useState } from 'react';
import { RefreshCw } from 'lucide-react';
import {
  ExternalBanking, ExternalEvent, OpsApiError, PtcConnectionTest, ReconciliationBatch,
  closeExternalEvent, getExternalBanking, listExternalEvents, listReconciliationBatches, runReconciliation, testPtcConnection,
} from './opsApi';
import { formatMoney } from '../money';
import { Busy, KeyValues, Loading, Panel, Status, Unavailable, dateTime, titleCase } from './shared';
import { Currency } from '../types';

const STATUS_LABEL: Record<string, string> = {
  RECORDED: 'Recorded', HELD_FOR_REVIEW: 'Held for review', REJECTED_INVALID: 'Rejected (invalid)',
  EXCEPTION: 'Exception', PROCESSED: 'Processed', CLOSED_NO_ACTION: 'Closed, no action',
};
const STATUS_TONE: Record<string, string> = {
  RECORDED: 'OPEN', HELD_FOR_REVIEW: 'PENDING', REJECTED_INVALID: 'FAILED', EXCEPTION: 'FAILED',
  PROCESSED: 'COMPLETED', CLOSED_NO_ACTION: 'CLOSED',
};
const FILTERS = [
  { key: 'NEEDS_ATTENTION', label: 'Needs attention' },
  { key: '', label: 'All events' },
] as const;

function eventAmount(e: ExternalEvent) {
  if (!e.amountMinor) return '—';
  return e.currency === 'CAD' ? formatMoney(e.amountMinor, e.currency as Currency) : `${e.amountMinor} minor units ${e.currency ?? ''}`;
}

const OUTCOME_TEXT: Record<PtcConnectionTest['outcome'], string> = {
  SUCCEEDED: 'Connected: authenticated and read the balance',
  AUTH_FAILED: 'Authentication failed',
  BALANCE_FAILED: 'Authenticated, but the balance request failed',
  NOT_CONFIGURED: 'Not configured',
  REFUSED: 'Not attempted',
};

/**
 * One deliberate, read-only check of the PTC EFT staging connection: a
 * Key Vault-signed token request (or the cached token) and GET /balance.
 * Nothing is created or changed at PTC or in MapleCoin.
 */
function ConnectionTest({ initial, onDone }: { initial: PtcConnectionTest | null; onDone: () => void }) {
  const [result, setResult] = useState<PtcConnectionTest | null>(initial);
  const [confirm, setConfirm] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const run = async () => {
    setBusy(true);
    setError(null);
    try {
      setResult(await testPtcConnection());
      setConfirm(false);
      onDone();
    } catch (err) {
      setError(err instanceof OpsApiError && err.status < 500 ? 'The test was refused.' : 'Operations is temporarily unavailable. Nothing was sent.');
    } finally {
      setBusy(false);
    }
  };
  const r = result;
  return (
    <div className="mco-confirm" role="group" aria-label="PTC connection test">
      {!confirm ? (
        <button type="button" className="mco-btn" onClick={() => setConfirm(true)}>Test connection</button>
      ) : (
        <>
          <p>This requests one PTC staging access token signed by MapleCoin's Key Vault key (or reuses the current one) and
            reads the EFT settlement balance. It is read-only: nothing is created or changed at PTC or in MapleCoin.</p>
          <div className="mco-actions">
            <button type="button" className="mco-btn mco-btn--primary" disabled={busy} onClick={run}><Busy busy={busy}>Run test</Busy></button>
            <button type="button" className="mco-btn mco-btn--quiet" disabled={busy} onClick={() => setConfirm(false)}>Cancel</button>
          </div>
        </>
      )}
      {error && <p className="mco-error" role="alert">{error}</p>}
      {r && (
        <div data-testid="ptc-test-result">
          <p className={`mco-check ${r.outcome === 'SUCCEEDED' ? 'mco-check--ok' : 'mco-check--bad'}`}>
            {OUTCOME_TEXT[r.outcome]} · {dateTime(r.ranAt)}
          </p>
          <KeyValues rows={[
            ['Result', r.message],
            ['Signing key', r.signer?.kind === 'KEY_VAULT' ? `Key Vault ${r.signer.vault} / ${r.signer.keyName} (version ${r.signer.keyVersion.slice(0, 8)}…)` : r.signer?.kind === 'LOCAL_PEM' ? 'Local development key' : '—'],
            ['Key ID (kid) sent', r.keyIdSent ? 'Yes' : 'No'],
            ['Token', r.token ? (r.token.ok
              ? `${r.token.newTokenRequested ? 'New token' : 'Reused cached token'} · lifetime ${r.token.lifetimeSeconds ?? '—'} s · issued ${dateTime(r.token.issuedAt)} · replaced after ${dateTime(r.token.refreshAt)}`
              : `Failed · ${r.token.error ?? ''}`) : 'Not attempted'],
            ['Token requests by this server', r.token ? String(r.token.tokenRequestsThisProcess) : '—'],
            ['Balance', r.balance ? (r.balance.ok
              ? <span key="bal" data-testid="ptc-balance">available {r.balance.availableBalance} · account {r.balance.accountBalance}</span>
              : `Failed · HTTP ${r.balance.httpStatus ?? '—'}${r.balance.errorCode ? ` · ${r.balance.errorCode}` : ''}`) : 'Not attempted'],
          ]} />
        </div>
      )}
    </div>
  );
}

function Connection({ data, onTested }: { data: ExternalBanking; onTested: () => void }) {
  const p = data.ptc;
  const env = data.providers.find((x) => x.provider === 'PTC')?.environment ?? null;
  const svc = (s: 'EFT' | 'INTERAC') => {
    const c = p.connection[s];
    if (!p.services[s].configured) return 'Not configured';
    if (!c.attempted) return 'Configured · not connected (no call made yet)';
    if (c.lastFailureAt && (!c.lastSuccessAt || c.lastFailureAt > c.lastSuccessAt)) return `Last attempt failed · ${c.lastFailure ?? ''}`;
    return `Connected · last token ${dateTime(c.lastSuccessAt)}`;
  };
  return (
    <Panel title="Payment provider" label="Payment provider">
      <KeyValues rows={[
        ['Provider', 'Peoples Trust Company / Peoples Group (PTC)'],
        ['Environment', <span key="env" data-testid="ptc-environment">{env === 'STAGING' ? 'Sandbox / staging' : env ?? '—'}</span>],
        ['Configuration', <span key="cfg" data-testid="ptc-configured">{p.problem ? `Problem: ${p.problem}` : p.configured ? 'Configured' : 'Not configured'}</span>],
        ['EFT (Funds Transfer)', <span key="eft" data-testid="ptc-eft">{svc('EFT')}</span>],
        ['INTERAC e-Transfer', <span key="interac" data-testid="ptc-interac">{svc('INTERAC')}</span>],
        ['INTERAC account endpoints (PTC → MapleCoin)', p.hostedInteracEndpointsEnabled ? 'Enabled' : 'Off'],
        ['EFT rejection webhook', p.eftRejectionWebhookEnabled ? 'Enabled' : 'Off'],
        ['Linked customers / accounts', `${data.linkedCustomers} / ${data.linkedAccounts}`],
      ]} />
      {p.services.EFT.configured && <ConnectionTest initial={p.lastConnectionTest ?? null} onDone={onTested} />}
      <p className="mco-note">
        Provider events are recorded as evidence and held for review; none is posted to customer balances yet.
        Sandbox Funding remains the development backing asset and is not a PTC balance.
      </p>
    </Panel>
  );
}

function EventRow({ e, onClosed }: { e: ExternalEvent; onClosed: (e: ExternalEvent) => void }) {
  const [closing, setClosing] = useState(false);
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const status = e.current?.status ?? '';
  const closable = ['HELD_FOR_REVIEW', 'EXCEPTION', 'REJECTED_INVALID'].includes(status);
  const submit = async () => {
    setBusy(true);
    setError(null);
    try {
      onClosed(await closeExternalEvent(e.eventSeq, reason.trim()));
      setClosing(false);
    } catch (err) {
      setError(err instanceof OpsApiError && err.status < 500 ? (err.detail ?? 'Refused.') : 'Operations is temporarily unavailable. Nothing was changed.');
    } finally {
      setBusy(false);
    }
  };
  return (
    <>
      <tr>
        <td className="mco-num">#{e.eventSeq}</td>
        <td className="mco-nowrap">{dateTime(e.receivedAt)}</td>
        <td>{titleCase(e.rail)}</td>
        <td>{titleCase(e.eventType)}</td>
        <td><code>{e.externalReference ?? '—'}</code></td>
        <td>{e.accountNumber ? <code>{e.accountNumber}</code> : <span className="mco-dim">not linked</span>}</td>
        <td className="mco-r mco-num">{eventAmount(e)}</td>
        <td>
          <Status value={STATUS_TONE[status] ?? 'CLOSED'} label={STATUS_LABEL[status] ?? status} />
          <div className="mco-dim">{titleCase(e.current?.reasonCode ?? '')}{e.conflictsWithEventSeq ? ` (conflicts with #${e.conflictsWithEventSeq})` : ''}</div>
        </td>
        <td className="mco-r">{closable && !closing && <button type="button" className="mco-link" onClick={() => setClosing(true)}>Close…</button>}</td>
      </tr>
      {closing && (
        <tr>
          <td colSpan={9}>
            <div className="mco-confirm" role="group" aria-label={`Close event ${e.eventSeq}`}>
              <label>Reason for closing with no action (required)
                <textarea value={reason} onChange={(x) => setReason(x.target.value)} maxLength={500} rows={2} />
              </label>
              <div className="mco-actions">
                <button type="button" className="mco-btn" disabled={busy || !reason.trim()} onClick={submit}>
                  <Busy busy={busy}>Close with no action</Busy>
                </button>
                <button type="button" className="mco-btn mco-btn--quiet" disabled={busy} onClick={() => setClosing(false)}>Cancel</button>
              </div>
              {error && <p className="mco-error" role="alert">{error}</p>}
            </div>
          </td>
        </tr>
      )}
    </>
  );
}

function Reconciliation() {
  const [batches, setBatches] = useState<ReconciliationBatch[] | null>(null);
  const [failed, setFailed] = useState(false);
  const [running, setRunning] = useState<string | null>(null);
  const load = useCallback(async () => {
    try {
      setBatches((await listReconciliationBatches()).batches);
      setFailed(false);
    } catch {
      setFailed(true);
    }
  }, []);
  useEffect(() => { load(); }, [load]);
  const rerun = async (seq: string) => {
    setRunning(seq);
    try {
      await runReconciliation(seq);
      await load();
    } finally {
      setRunning(null);
    }
  };
  return (
    <Panel title="Reconciliation" label="Reconciliation">
      {failed && <Unavailable onRetry={load} />}
      {!failed && !batches && <Loading />}
      {batches && batches.length === 0 && <p className="mco-empty" data-testid="no-reports">No provider reports received yet.</p>}
      {batches?.map((b) => (
        <article key={b.batchSeq} className="mco-intervention" aria-label={`Report ${b.batchSeq}`}>
          <header>
            <strong>{titleCase(b.sourceType)} · {b.activityDate}</strong>
            <button type="button" className="mco-btn" disabled={running !== null} onClick={() => rerun(b.batchSeq)}>
              <Busy busy={running === b.batchSeq}>Run reconciliation</Busy>
            </button>
          </header>
          <p className="mco-dim">{b.itemCount} line{b.itemCount === 1 ? '' : 's'} · received {dateTime(b.receivedAt)}{b.sourceName ? ` · ${b.sourceName}` : ''}</p>
          {b.latestRun ? (
            <dl className="mco-counts">
              {Object.entries(b.latestRun.counts).map(([k, v]) => (
                <div key={k}><dt>{titleCase(k)}</dt><dd className="mco-num">{v}</dd></div>
              ))}
            </dl>
          ) : <p className="mco-note">Not reconciled yet.</p>}
        </article>
      ))}
    </Panel>
  );
}

export default function ExternalBankingTab() {
  const [data, setData] = useState<ExternalBanking | null>(null);
  const [events, setEvents] = useState<ExternalEvent[]>([]);
  const [filter, setFilter] = useState<string>('NEEDS_ATTENTION');
  const [state, setState] = useState<'loading' | 'ready' | 'error'>('loading');
  const [refreshing, setRefreshing] = useState(false);

  const load = useCallback(async () => {
    setRefreshing(true);
    try {
      const [summary, list] = await Promise.all([getExternalBanking(), listExternalEvents({ limit: '100' })]);
      setData(summary);
      setEvents(list.events);
      setState('ready');
    } catch {
      setState('error');
    } finally {
      setRefreshing(false);
    }
  }, []);
  useEffect(() => { load(); }, [load]);

  if (state === 'loading') return <Loading label="Loading external banking" />;
  if (state === 'error' || !data) return <Unavailable onRetry={load} />;

  const attention = ['HELD_FOR_REVIEW', 'EXCEPTION', 'REJECTED_INVALID'];
  const shown = filter === 'NEEDS_ATTENTION' ? events.filter((e) => attention.includes(e.current?.status ?? '')) : events;
  return (
    <div className="mco-stack">
      <div className="mco-asof">
        <span>External events received: {data.events.total}{data.events.lastReceivedAt ? ` · last ${dateTime(data.events.lastReceivedAt)}` : ''}</span>
        <button type="button" className="mco-btn mco-btn--quiet" onClick={load} disabled={refreshing}>
          <Busy busy={refreshing}><RefreshCw size={14} aria-hidden /> Refresh</Busy>
        </button>
      </div>
      <Connection data={data} onTested={load} />

      <Panel title="External events" label="External events">
        <div className="mco-segments" role="tablist">
          {FILTERS.map((f) => (
            <button key={f.key || 'all'} type="button" role="tab" aria-selected={filter === f.key}
              className={`mco-seg${filter === f.key ? ' mco-seg--on' : ''}`} onClick={() => setFilter(f.key)}>{f.label}</button>
          ))}
        </div>
        {data.events.total === 0
          ? <p className="mco-empty" data-testid="no-external-data">No external data yet. Nothing has been received from PTC.</p>
          : (
            <div className="mco-table-wrap">
              <table className="mco-table">
                <thead><tr><th>#</th><th>Received</th><th>Rail</th><th>Event</th><th>Provider ref</th><th>Account</th>
                  <th className="mco-r">Amount</th><th>Handling</th><th /></tr></thead>
                <tbody>
                  {shown.length === 0 && <tr><td colSpan={9} className="mco-empty">Nothing needs attention.</td></tr>}
                  {shown.map((e) => (
                    <EventRow key={e.eventSeq} e={e} onClosed={(c) => setEvents((prev) => prev.map((x) => (x.eventSeq === c.eventSeq ? c : x)))} />
                  ))}
                </tbody>
              </table>
            </div>
          )}
      </Panel>

      <Reconciliation />

      <Panel title="Handling policy" label="Handling policy">
        <p className="mco-note">How each kind of provider event is handled today. Posting to customer balances is not enabled for any event type.</p>
        <div className="mco-table-wrap">
          <table className="mco-table">
            <thead><tr><th>Rail</th><th>Event</th><th>Handling</th><th>Meaning</th></tr></thead>
            <tbody>
              {data.policies.map((p) => (
                <tr key={`${p.rail}:${p.eventType}`}>
                  <td>{titleCase(p.rail)}</td><td>{titleCase(p.eventType)}</td>
                  <td>{p.action === 'RECORD_ONLY' ? 'Record only' : 'Hold for review'}</td><td className="mco-dim">{p.description}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Panel>
    </div>
  );
}
